/**
 * Compiles a StyleDef into the shape Webflow's clipboard format expects:
 * a `styleLess` string for the base breakpoint, plus `variants` holding only the deltas.
 */

import { assertLonghand, serialise } from './longhand.js';
import {
  parseVariantKey,
  resolveCascade,
  toEmitVariantKey,
  VARIANT_EMIT_ORDER,
  type WfVariantKey,
} from './cascade.js';
import type { StyleDef } from './styleDef.js';

export interface CompiledStyle {
  styleLess: string;
  variants: Record<string, { styleLess: string }>;
}

/** Deterministic key order, so golden snapshots diff cleanly when a style changes. */
function orderVariantKeys(keys: WfVariantKey[]): WfVariantKey[] {
  const rank = (k: WfVariantKey): number => {
    const { breakpoint, pseudo } = parseVariantKey(k);
    const bpRank = breakpoint === 'main' ? -1 : VARIANT_EMIT_ORDER.indexOf(breakpoint);
    // Plain breakpoints first, then pseudo-states, then the combined forms.
    return (pseudo ? 100 : 0) + (bpRank < 0 ? 0 : bpRank);
  };
  return [...keys].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

export function compileStyleLess(def: StyleDef): CompiledStyle {
  assertLonghand(def.base, `${def.name} (base)`);

  const resolved = resolveCascade({ base: def.base, ...(def.variants ? { variants: def.variants } : {}) });

  const variants: Record<string, { styleLess: string }> = {};
  for (const key of orderVariantKeys(Object.keys(resolved) as WfVariantKey[])) {
    const decl = resolved[key];
    if (!decl) continue;
    assertLonghand(decl, `${def.name} (${key})`);
    const styleLess = serialise(decl);
    // Normalise on the way out: a bare `hover` is valid to author but invalid on the wire.
    if (styleLess) variants[toEmitVariantKey(key)] = { styleLess };
  }

  return { styleLess: serialise(def.base), variants };
}
